import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment, { Moment } from "moment";
import { gql } from "__generated__/gql";
import { useLazyQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { PageHeader, Card } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";
import { Grid, Tabs, Tab, Alert } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import PeriodField, { getPeriodWindow, PeriodWindow } from "components/form/PeriodField";
import Helmet from "components/helmet/Helmet";
import { CharterSelector } from "components/declaration/mutualComponents";

import { Language, hasPluginPermission } from "imports/Utils";
import { useStoreState } from "ezStore";

import DeclarationLines from "./DeclarationLines";
import InvoiceLines from "./Invoicelines";
import Remark from "./Remark";
import SelectableSalesCharters from "./SelectableSalesCharters";
import CharterCargos from "./CharterCargos";

import { Charter, SalesCharter } from "types/index";

import "./declarations.scss";
import DeclarationViewButtons, { DeclarationView } from "view/declarations/components/buttons/DeclarationViewButtons";
import { SailingReportRangeType } from "view/sailingreports/utils/SailingReportUtils.tsx";

const TABS = {
	DECLARATION: "declaration",
	COST_LINES: "costLines",
};

type SaleDeclarationState = {
	charter?: SalesCharter;
	loading: boolean;
	collapsed: boolean;
	selectedTab: string;
	selectedCharterId?: number;
	selectableCharters: Charter[];
	updateSaleCharterRequestId: number;
	window: {
		from: Moment;
		to: Moment;
	}
};

const SaleDeclarations = () => {

	const [ searchParams ] = useSearchParams();

	const navigate = useNavigate();

	const session = useStoreState(state => state.session);
	const period = hasPluginPermission(session, "declarationOverviewMonth") ? SailingReportRangeType.Month : SailingReportRangeType.Week;

	const [ state, setState ] = useState<SaleDeclarationState>({
		charter: undefined,
		loading: false,
		collapsed: false,
		selectedTab: TABS.DECLARATION,
		selectedCharterId: searchParams.get("charter") ? Number(searchParams.get("charter")) : undefined,
		selectableCharters: [],
		updateSaleCharterRequestId: 0,
		window: getPeriodWindow(searchParams.get("from") ? Number(searchParams.get("from")) : undefined, period),
	});

	const [ getCharter, { refetch } ] = useLazyQuery(CHARTER_QUERY, {
		onCompleted: response => {

			const charter = response.charter as SalesCharter;
			const validUntil = charter.contract?.validUntil;
			const validFrom = charter.contract?.validFrom;

			if (validUntil && (validUntil <= moment(state.window.from).unix() * 1000)) {
				setState(oldState => ({
					...oldState,
					loading: false,
					charter: undefined,
					selectedTab: TABS.DECLARATION,
					selectableCharters: [],
					selectedCharterId: undefined,
				}));
			} else if (validFrom && (validFrom >= moment(state.window.to).unix() * 1000)) {
				setState(oldState => ({
					...oldState,
					loading: false,
					charter: undefined,
					selectedTab: TABS.DECLARATION,
					selectableCharters: [],
					selectedCharterId: undefined,
				}));
			} else {
				setState(oldState => ({
					...oldState,
					loading: false,
					charter: charter,
					selectedTab: charter.declarations?.[0] ? TABS.COST_LINES : TABS.DECLARATION,
				}));
			}
		},
		onError: () => {
			setState(oldState => ({
				...oldState,
				loading: false,
				charter: undefined,
			}));
		},
	});

	const handleWindowChange = (newWindow: PeriodWindow) => setState(oldState => ({
		...oldState,
		window: {
			from: newWindow.from,
			to: newWindow.to,
		},
	}));

	const handleUrl = () => {
		const from = moment(state.window.from).unix() * 1000;
		const to = moment(state.window.to).unix() * 1000;
		navigate(`/view/declarations/sale?${state.selectedCharterId ? `charter=${state.selectedCharterId}&` : ""}from=${from}&to=${to}`);
	};

	const handleSelectCharter = (id: number) => {
		setState(oldState => ({
			...oldState,
			loading: id ? true : false,
			selectedCharterId: id,
		}));

		if (id) {
			getCharter({
				variables: {
					id: id,
					from: moment(state.window.from).unix() * 1000,
					to: moment(state.window.to).unix() * 1000,
					onlyDeclarableAgreements: true,
				},
			});
		}
	};

	const setLoading = (loadingState: boolean) => {
		setState(oldState => ({
			...oldState,
			loading: loadingState,
		}));
	};

	useEffect(() => {
		handleUrl();
	}, [ state.selectedCharterId, state.window ]);

	usePropertyChangeEffect(() => {
		if (state.charter) {
			handleSelectCharter(state.charter.id);
		}
	}, [ state.window.from, state.window.to ]);

	useEffect(() => {
		if (searchParams.get("charter")) {
			handleSelectCharter(Number(searchParams.get("charter")));
		}
	}, []);

	const actuallyUpdateSaleCharterRequestId = () => setState({ ...state, updateSaleCharterRequestId: state.updateSaleCharterRequestId + 1 });

	return (
		<div className="declarations-page">
			<PageHeader>
				<PageHeader.Title>
					{Language("ADMINISTRATION_SALE_DECLARATIONS")}
					<Helmet title={intl.formatMessage({ id: "ADMINISTRATION_SALE_DECLARATIONS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<PeriodField
						period={period}
						disabled={state.loading}
						defaultStart={state.window.from}
						onChange={handleWindowChange} />

					<CharterSelector
						disabled={state.loading}
						selectedCharterId={state.selectedCharterId}
						currentCharters={state.selectableCharters}
						onChange={(charter: Charter) => handleSelectCharter(charter.id)}
						renderField={(charter: Charter) => charter.charterNumber} />

					<DeclarationViewButtons active={DeclarationView.Time} basePath="/view/declarations/sale" />
				</PageHeader.Actions>
			</PageHeader>

			<div className={`declaration-container ${state.collapsed ? "collapsed" : ""}`}>
				<div className="sidebar">
					<div className="sidebar-content">
						<div className="sidebar-content-opacity">
							<div className="purchase-charters">
								<SelectableSalesCharters
									selectedCharterId={state.selectedCharterId}
									onSelect={(id) => handleSelectCharter(id)}
									onChange={(charters: Charter[]) => {
										setState(oldState => ({
											...oldState,
											selectableCharters: charters,
										}));
									}}
									setLoading={setLoading}
									window={state.window}
									actuallyUpdateSaleCharterRequestId={actuallyUpdateSaleCharterRequestId}
									updateSaleCharterRequestId={state.updateSaleCharterRequestId} />
							</div>
						</div>
					</div>

					<CollapseButton
						collapsed={state.collapsed}
						onClick={() => {
							setState(oldState => ({
								...oldState,
								collapsed: !state.collapsed,
							}));
						}} />
				</div>

				<div className="content">
					{(state.charter) ? (
						<React.Fragment>
							<div className="sales-charters" style={{ marginBottom: 24 }}>
								<Card disablePadding>
									<CharterCargos
										charter={state.charter}
										loading={state.loading}
										saleCharterRequestId={state.updateSaleCharterRequestId}
										refetch={() => {
											setState(oldState => ({
												...oldState,
												loading: true,
											}));

											refetch();
										}} />
								</Card>
							</div>

							<Grid spacing={3} container>
								<Grid xs={12} item>
									<Card disablePadding>
										<Tabs
											value={state.selectedTab}
											onChange={(event, newValue) => {
												setState(oldState => ({
													...oldState,
													selectedTab: newValue,
												}));
											}}>
											<Tab label={Language("DECLARATION")} value={TABS.DECLARATION} />
											<Tab label={Language("COST_LINES")} value={TABS.COST_LINES} />
										</Tabs>
									</Card>
								</Grid>

								<Grid
									xs={12}
									md={12}
									lg={12}
									xl={7}
									item>
									<Card>
										{state.selectedTab === TABS.DECLARATION && (
											<DeclarationLines
												charterId={state.charter.id}
												window={state.window}
												hideSaveButton={undefined}
												invoiceGroupType={undefined}
												onStateChange={undefined}
												refetch={() => {
													setState(oldState => ({
														...oldState,
														loading: true,
														selectedTab: TABS.COST_LINES,
														updateSaleCharterRequestId: oldState.updateSaleCharterRequestId + 1,
													}));

													refetch();
												}} />
										)}

										{state.selectedTab === TABS.COST_LINES && (
											<InvoiceLines
												charter={state.charter}
												loading={state.loading}
												window={state.window}
												deletable
												refetch={() => {
													setState(oldState => ({
														...oldState,
														loading: true,
														updateSaleCharterRequestId: oldState.updateSaleCharterRequestId + 1,
													}));

													refetch();
												}}
												disableRowSelection={undefined} />
										)}
									</Card>
								</Grid>

								<Grid
									xs={12}
									md={12}
									lg={12}
									xl={5}
									item>
									<Card>
										<Remark
											charter={state.charter}
											window={state.window}
											refetch={refetch} />
									</Card>
								</Grid>
							</Grid>
						</React.Fragment>
					) : (
						<React.Fragment>
							<Alert severity="warning">{Language("SELECT_SALES_CHARTER")}</Alert>
						</React.Fragment>
					)}
				</div>
			</div>
		</div>
	);
};

const CollapseButton = ({ collapsed, onClick }: { collapsed: boolean, onClick: () => void }) => {
	return (
		<div className="collapse-button" onClick={onClick}>
			{collapsed ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
		</div>
	);
};

export const CHARTER_QUERY = gql(`#graphql
	query saleCharter_declarations($id: Long!, $from: Instant, $to: Instant, $onlyDeclarableAgreements : Boolean) {
		charter(id: $id) {
			id
			creditor {
				id
				name
			}
			type
			... on SalesCharter {
				declarations(from: $from, to: $to) {
					id
					correction
					declarationValues {
						id
						agreement {
							id
							summary
							description
							priority
							calculateAmount(charterId: $id, from: $from, to: $to)
						}
						quantity
					}
					remark
					closedOn
					financiallyReadiedOn
					invoiceLines {
						id
						approved
						invoiceReference
						description
						quantity
						unit
						price {
							value
							currency {
								isoCode
								rate
								symbol
							}
						}
						creditor {
							id
							name
						}
						debtor {
							id
							name
						}
						costType {
							id
							title_en
							title_nl
							title_de
						}
						agreement {
							id
						}
						unexpected
					}
				}
				cargos {
					id
					cargoNumber
					containerNumber
					containerType {
						isoTypeCode
						displayCode
					}
					lastStatusChange {
						...statusChangeFragment
					}
					product {
						id
						code
						name
						description
						adnAttributes {
							unNumber
						}
					}
					firstLoadingAction {
						...CargoActionWithVoyage
					}
					lastDischargingAction {
						...CargoActionWithVoyage
					}
					teu
					weight
					volume
				}
			}
			contract {
				id
				contractNumber
				validFrom
				validUntil
				isDeclarable
				agreements(onlyDeclarableAgreements : $onlyDeclarableAgreements, charterId : $id) {
					id
					calculateAmount(charterId: $id, from: $from, to: $to)
					summary
					description
					priority
					type
					costType {
						id
						title_en
						title_nl
						title_de
					}
					calculationMethod {
						id
						type
						price
						minimumPrice
						minimumQuantityPayable
						preAgreed
						payableOver
						description
					}
				}
			}
			supplierCharters(from: $from, to: $to, includeLocationOwners: true) {
				id
				creditor {
					id
					name
				}
				contract {
					id
					agreements(onlyDeclarableAgreements : $onlyDeclarableAgreements, charterId : $id) {
						id
						summary
						description
						priority
						type
						costType {
							id
							title_en
							title_nl
							title_de
						}
						calculationMethod {
							id
							type
							price
							minimumPrice
							minimumQuantityPayable
							preAgreed
							payableOver
							description
						}
					}
				}
			}
		}
	}
`);

export default SaleDeclarations;
